import { apiRequest } from "@/services/api/apiClient.js";
import { deveTentarFormatoAntigo, lerPedidoDeVolta, montarLinkDeSso, montarLinkDeTicket } from "@/services/sso.js";

/**
 * Aplicacoes da plataforma que o usuario logado pode abrir nesta corporacao.
 *
 * A lista vem do registry no banco (`GET /apps` do liberty/auth), nao de
 * variavel de ambiente: aplicacao nova aparece no menu sem deploy do admin. O
 * recorte por permissao e por corporacao ja vem feito do servidor, e a `url` de
 * cada uma ja vem resolvida para o ambiente.
 */
export const listAvailableApps = async () => {
    try {
        const response = await apiRequest({
            client: window.API_AUTH,
            method: "get",
            url: "/apps",
        });

        return response?.data ?? [];
    } catch (error) {
        // API de auth anterior ao registry (rota inexistente) ou fora do ar: o
        // menu segue sem a secao de aplicacoes, em vez de nao carregar.
        console.warn("Nao foi possivel listar as aplicacoes:", error);
        return [];
    }
};

/**
 * Pede ao auth um ticket de entrada para a aplicacao.
 *
 * O destino interno vai no pedido, e nao na URL: quem guarda para onde levar o
 * usuario e o servidor, junto do ticket.
 */
export const requestSsoTicket = async (slug, destino = null) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: "/sso/ticket",
        body: destino ? { app: slug, r: destino } : { app: slug },
    });
};

/**
 * Leva o usuario para a aplicacao.
 *
 * Caminho normal: pede o ticket e usa o destino que o servidor resolveu no
 * registry. Se este ambiente ainda tiver uma API de auth sem `POST /sso/ticket`
 * (ou ela estiver fora do ar), cai no formato anterior, com o token no
 * fragmento -- e so nesse caso: recusa explicita do servidor **nao** vira
 * fallback, senao o formato antigo passaria por cima da checagem de permissao.
 *
 * A navegacao e feita por codigo, e nao por `href`, porque o link carrega
 * credencial: no atributo ela apareceria na barra de status do navegador e
 * sairia junto em qualquer "copiar endereco do link".
 */
export const abrirAplicacao = async (app, { destino } = {}) => {
    try {
        const resposta = await requestSsoTicket(app?.slug, destino);
        const link = montarLinkDeTicket({
            redirectUrl: resposta?.redirect_url,
            ticket: resposta?.ticket,
        });

        if (link) {
            window.location.assign(link);
            return true;
        }
    } catch (error) {
        if (!deveTentarFormatoAntigo(error)) {
            console.error("Entrada recusada para a aplicacao:", app?.slug, error);
            return false;
        }

        console.warn("Auth sem emissao de ticket; usando o formato anterior:", error);
    }

    const legado = montarLinkDeSso({
        url: app?.url,
        token: localStorage.getItem("authToken"),
        corp: localStorage.getItem("corp"),
        destino,
    });

    if (!legado) {
        console.error("Sem sessao ou sem URL para abrir a aplicacao:", app?.slug);
        return false;
    }

    window.location.assign(legado);
    return true;
};

/**
 * Onde o pedido de volta espera o login terminar.
 *
 * Nao da para carregar isso na URL: entre a chegada e a sessao pronta a pessoa
 * pode passar por segundo fator e por escolha de empresa, e cada um desses
 * passos e um `router.push` que descarta a query. `sessionStorage` e por aba,
 * some quando ela fecha, e nao vaza para outra janela.
 */
const CHAVE_PEDIDO_DE_VOLTA = "sso:pedido-de-volta";

/**
 * Guarda o pedido de volta que veio na URL e limpa a barra de enderecos.
 *
 * Chamado uma vez, na carga da aplicacao, antes do router. Limpar a URL evita
 * que um F5 depois do login refaca o pedido -- que levaria a pessoa embora de
 * onde ela tivesse chegado.
 */
export const guardarPedidoDeVolta = () => {
    const pedido = lerPedidoDeVolta(window.location.search);

    if (!pedido) {
        return null;
    }

    try {
        window.sessionStorage.setItem(CHAVE_PEDIDO_DE_VOLTA, JSON.stringify(pedido));
    } catch (error) {
        // Aba sem sessionStorage: sem pedido guardado, o login termina na home
        // do admin. Perde-se a conveniencia, nao o acesso.
        console.warn("Nao foi possivel guardar o pedido de volta:", error);
        return null;
    }

    window.history.replaceState(null, "", window.location.pathname);

    return pedido;
};

/**
 * Leva a pessoa de volta para a aplicacao que pediu o login, se houve pedido.
 *
 * Roda depois que a sessao esta pronta. O pedido e consumido **antes** de
 * abrir a aplicacao: se a emissao do ticket falhar, a pessoa fica no admin com
 * uma mensagem no console, em vez de bater na mesma pedra a cada navegacao.
 *
 * O slug e conferido contra a lista de aplicacoes que esta pessoa pode abrir.
 * Nao e a checagem de seguranca -- essa e do servidor, na emissao do ticket --,
 * e sim o que evita mandar alguem para um erro: sem permissao, ou com um slug
 * que nao existe mais, o login simplesmente termina na home.
 */
export const retomarPedidoDeVolta = async () => {
    let pedido = null;

    try {
        const guardado = window.sessionStorage.getItem(CHAVE_PEDIDO_DE_VOLTA);

        if (!guardado) {
            return false;
        }

        window.sessionStorage.removeItem(CHAVE_PEDIDO_DE_VOLTA);
        pedido = JSON.parse(guardado);
    } catch (error) {
        console.warn("Pedido de volta ilegivel; seguindo para a home:", error);
        return false;
    }

    if (!pedido?.slug) {
        return false;
    }

    const aplicacoes = await listAvailableApps();
    const aplicacao = aplicacoes.find((item) => item?.slug === pedido.slug);

    if (!aplicacao) {
        console.warn("Aplicacao do pedido de volta indisponivel para este usuario:", pedido.slug);
        return false;
    }

    return abrirAplicacao(aplicacao, { destino: pedido.destino });
};
